import React, { memo, useEffect, useRef } from 'react';
import { classNames } from '../../utils/classNames';
import { AttachmentPreviewRow } from './AttachmentPreviewRow';
import type { AttachmentPreviewPopoverProps } from './AttachmentPreviewPopover.types';
import './AttachmentPreviewPopover.css';

/**
 * Renders below its parent (upload wrap has position: relative). Same pattern as OptionsMenu dropdown.
 */
function AttachmentPreviewPopoverComponent({
  attachments,
  open,
  onClose,
  onRemove,
  onFileClick,
  className
}: AttachmentPreviewPopoverProps): React.ReactElement | null {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={popoverRef}
      className={classNames('attachment-preview-popover', className)}
      role="dialog"
      aria-label="Attached files"
    >
      <div className="attachment-preview-popover__header">
        {attachments.length} file{attachments.length !== 1 ? 's' : ''}
      </div>
      <div className="attachment-preview-popover__list">
        {attachments.map((item, i) => (
          <AttachmentPreviewRow
            key={item.id}
            item={item}
            index={i}
            onRemove={onRemove}
            onFileClick={onFileClick}
          />
        ))}
      </div>
    </div>
  );
}

export const AttachmentPreviewPopover = memo(AttachmentPreviewPopoverComponent);
AttachmentPreviewPopover.displayName = 'AttachmentPreviewPopover';
